// @givmo/board — the way from a closed session's public entry to its own minutes (CBG-RC12-READ,
// frames `d1-entry`, `d2-open`, `d3-opened`; backend `board_minutes.md` §O7).
//
// ══ WHERE "OPEN ITS MINUTES" GOES ══════════════════════════════════════════════════════════════
//
// Minutes SENT TO this director open as any record does. Minutes they weren't sent are OPENED as a
// current director, and that read is recorded in their history — so it is a deliberate step, never a
// link that just opens: the record page first shows the step (d2), built from the public entry alone,
// and reads the record only once "Open the minutes" is pressed. The link carries the meeting and the
// session (`?meeting=&session=`) so the step can say what it opens without reading it.
//
// ══ WITH THE SIGNING WINDOW SHUT ═══════════════════════════════════════════════════════════════
//
// The backend withholds whether a closed session was sent to this director, and its minutes'
// pointer, while the window is shut (§O12 C5): who was in a closed session is closed-session content.
// A shut window shows no chip and there is no "Confirm" button anywhere (Jonathan, 2026-10-02), so
// "Open its minutes" brings the confirmation up by itself — the read gate's own sheet — then reads the
// meeting again and goes on (the orchestrator, 2026-10-13 11:54 PDT):
//   * not sent to them → the step (d2);
//   * sent to them → the minutes;
//   * no minutes stand yet → nowhere: the entry now shows its facts.
// A dismissed confirmation leaves the page as it was. Nothing reads either withheld fact before the
// confirmation, and after it the meeting is read again, never taken from before (the rail).
//
// During the pause after a sign-in method is removed (CBG-FIX127; `director/signing-pause.ts`) no
// confirmation can open it: "Open its minutes" brings up no sheet and says when it opens (S2).

import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@givmo/design-system';
import { useNavigate } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import { directorBoardSearch } from '../../director/director-board';
import {
  boardDirectorMeKey,
  decodeDirectorMe,
  directorBoardFor,
  useDirectorMe,
} from '../../director/director-me';
import { isCommitteeOnly } from '../../director/director-wire';
import { DID_NOT_OPEN } from '../../director/read-gate';
import { openExecutiveRowFor } from '../../director/signing-copy';
import { BoardSigningWindowShut, useBoardSigningGate } from '../../director/signing-gate';
import { signingPauseWords, useSigningPause } from '../../director/signing-pause';
import {
  closedMinutesWay,
  decodeDirectorMeetingRecord,
  type DirectorMeetingRecord,
} from './director-minutes-decode';

/** The record page's search keys for the step: the meeting, and the closed session in it. */
export const CLOSED_MINUTES_MEETING_KEY = 'meeting';
export const CLOSED_MINUTES_SESSION_KEY = 'session';

/** The ONE key of a director's read of one meeting's record — the minutes panel's and the step's. */
export function directorMeetingRecordKey(orgRef: string | null, meetingId: string | null) {
  return ['board-director-meeting-record', orgRef, meetingId] as const;
}

/** Where "Open its minutes" goes for minutes this director may open. */
export function closedMinutesDestination(
  orgRef: string,
  meetingId: string,
  sessionId: string,
  way: { recordId: string; sentToYou: boolean },
) {
  return {
    to: BOARD_DIRECTOR_PAGES.record,
    params: { recordId: way.recordId },
    search: way.sentToYou
      ? directorBoardSearch(orgRef)
      : {
          ...directorBoardSearch(orgRef),
          [CLOSED_MINUTES_MEETING_KEY]: meetingId,
          [CLOSED_MINUTES_SESSION_KEY]: sessionId,
        },
  };
}

export interface ClosedMinutesOpening {
  /** Confirm, read the meeting again, and go where its minutes are (see the header). */
  ask: (sessionId: string) => Promise<void>;
  /** The session whose confirmation is under way, or null. */
  asking: string | null;
  /** True while the director is at the sheet (the act rests behind it, not spinning). */
  prompting: boolean;
  dialog: ReactNode;
}

/** The shut-window opening for one meeting's closed sessions (see the header). */
export function useClosedMinutesOpening(
  orgRef: string | null,
  meetingId: string,
): ClosedMinutesOpening {
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const navigate = useNavigate();
  const meQuery = useDirectorMe();
  // CBG-FIX106 (row 4.11): a committee's closed-session minutes, to a seat for someone who isn't on
  // the board, in the committee's words.
  const gate = useBoardSigningGate(
    openExecutiveRowFor(isCommitteeOnly(directorBoardFor(meQuery.data, orgRef))),
  );
  const [asking, setAsking] = useState<string | null>(null);
  const pauseEnd = useSigningPause();

  const ask = async (sessionId: string) => {
    if (orgRef === null || asking !== null) return;
    if (pauseEnd !== null) {
      // CBG-FIX127 (S2): no sheet; `/me` is asked again, so a later removal's end replaces this one.
      toast.error(signingPauseWords.reads(pauseEnd));
      void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
      return;
    }
    setAsking(sessionId);
    try {
      const outcome = await gate.run(async (): Promise<DirectorMeetingRecord> => {
        // The server's answer about the window, read afresh with this moment's token.
        const me = await queryClient.fetchQuery({
          queryKey: boardDirectorMeKey(),
          staleTime: 0,
          queryFn: async () => {
            const { data } = await operations.directorGetBoardDirectorMe({ throwOnError: true });
            return decodeDirectorMe(data);
          },
        });
        if (!me.signing.open) throw new BoardSigningWindowShut();
        // Read again now the window is open: never a value from before the confirmation.
        const { data } = await operations.directorGetBoardDirectorMeetingRecord({
          path: { org_ref: orgRef, meeting_id: meetingId },
          throwOnError: true,
        });
        const record = decodeDirectorMeetingRecord(data);
        queryClient.setQueryData(directorMeetingRecordKey(orgRef, meetingId), record);
        return record;
      }, meQuery.data?.signing.account_has_passkey);
      if (outcome.kind === 'cancelled') return;
      if (outcome.kind !== 'done') {
        toast.error(DID_NOT_OPEN);
        return;
      }
      const entry = outcome.value.sessionEntries?.find((row) => row.id === sessionId);
      const way = entry === undefined ? null : closedMinutesWay(entry);
      if (way?.kind !== 'open') return;
      await navigate(closedMinutesDestination(orgRef, meetingId, sessionId, way));
    } finally {
      setAsking(null);
    }
  };

  return { ask, asking, prompting: gate.prompting, dialog: gate.dialog };
}
